import React, { useState } from 'react';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import { billingPurchaseCredits } from '@api/billing';
import Field, { FIELD_SIZE_COMPACT } from '@components/1-atoms/Field';
import {
  Container,
  Row,
  FieldWrapper,
  PriceLabel,
  SmallButton,
  Description,
  UpgradeMessage,
  UpgradeLink,
} from './CreditPurchasePrompt.styled';

const MIN_QUANTITY = 100;
const MAX_QUANTITY = 5000;

const clamp = (n: number) => Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, n));

interface CreditPurchasePromptProps {
  /** When explicitly false (free tier), show an upgrade CTA instead of the credit purchase form.
   *  null/undefined are treated the same as true — show the credit purchase form by default. */
  hasActiveSubscription?: boolean | null;
}

const CreditPurchasePrompt: React.FC<CreditPurchasePromptProps> = ({
  hasActiveSubscription,
}) => {
  const { user } = useAuth();
  const { showError } = useSnackbar();
  const [rawValue, setRawValue] = useState(String(MIN_QUANTITY));
  const [purchasing, setPurchasing] = useState(false);

  const quantity = (() => {
    const parsed = Number.parseInt(rawValue, 10);
    return Number.isNaN(parsed) ? MIN_QUANTITY : clamp(parsed);
  })();

  const handleQuantityChange = (val: string) => {
    setRawValue(val.replaceAll(/\D/g, ''));
  };

  const handleBlur = () => {
    setRawValue(String(quantity));
  };

  const handlePurchase = async () => {
    if (!user?.token) return;
    setPurchasing(true);
    try {
      const { checkoutUrl } = await billingPurchaseCredits(user.token, quantity);
      globalThis.open(checkoutUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to start purchase');
    } finally {
      setPurchasing(false);
    }
  };

  if (hasActiveSubscription === false) {
    return (
      <Container>
        <UpgradeMessage>
          You&apos;ve used all your free credits.{' '}
          <UpgradeLink to="/settings/billing" data-testid="upgrade-link">Upgrade your plan</UpgradeLink>{' '}
          to continue.
        </UpgradeMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Row>
        <FieldWrapper>
          <Field
            dataTestId="credit-quantity"
            type="number"
            min={MIN_QUANTITY}
            max={MAX_QUANTITY}
            value={rawValue}
            onChange={handleQuantityChange}
            onBlur={handleBlur}
            passFieldValueOnChange
            size={FIELD_SIZE_COMPACT}
          />
        </FieldWrapper>
        <PriceLabel>= ${quantity.toLocaleString()}</PriceLabel>
        <SmallButton
          dataTestId="purchase-credits-btn"
          onClick={handlePurchase}
          disabled={purchasing}
          loading={purchasing}
          stretch={false}
        >
          Purchase Credits
        </SmallButton>
      </Row>
      <Description>
        On-demand credits at $1.00 each. Opens Stripe checkout in a new tab.
      </Description>
    </Container>
  );
};

export default CreditPurchasePrompt;
